import * as React from 'react';
import { Text } from '../../display';
import { getLabelColor, buildContainerClasses, controlBaseClasses } from '../shared';

/**
 * Size variants for the Radio
 * - sm: 16px
 * - md: 18px (default, matches spec)
 * - lg: 22px
 */
export type RadioSize = 'sm' | 'md' | 'lg';

/**
 * Radio component props
 * Extends native input attributes, omitting 'type' and 'size' to avoid conflicts
 */
export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /**
   * Label text displayed next to the radio
   */
  label?: string;
  /**
   * Size variant of the radio
   * @default 'md'
   */
  size?: RadioSize;
  /**
   * Whether the radio is in an error state
   */
  error?: boolean;
  /**
   * Helper text displayed below the radio
   */
  helperText?: string;
}

/**
 * Size classes for the outer radio circle.
 */
const sizeClasses: Record<RadioSize, { outer: string }> = {
  sm: { outer: 'w-4 h-4' }, // 16px
  md: { outer: 'w-[18px] h-[18px]' }, // 18px (spec)
  lg: { outer: 'w-[22px] h-[22px]' }, // 22px
};

/**
 * Base classes for the outer radio circle (always applied)
 */
const outerBaseClasses = [...controlBaseClasses, 'rounded-full'].join(' ');

/**
 * Get outer circle state classes based on disabled and error state.
 *
 * When checked the whole circle fills with the state's colour — mirrors
 * the Checkbox pattern (`peer-checked:bg-primary-700 peer-checked:border-primary-700`)
 * so the two controls read as a consistent family. No inner indicator:
 * the solid fill + coloured ring carry the selected state on their own.
 */
function getOuterStateClasses(disabled: boolean, error: boolean): string {
  if (disabled) {
    return 'border-secondary-300 bg-secondary-100 peer-checked:bg-secondary-400 peer-checked:border-secondary-400';
  }
  if (error) {
    return 'border-error-700 peer-checked:bg-error-700 peer-checked:border-error-700';
  }
  return 'border-secondary-400 peer-checked:bg-primary-700 peer-checked:border-primary-700';
}

/**
 * Get focus classes (only when not disabled)
 */
function getFocusClasses(disabled: boolean): string {
  if (disabled) {
    return '';
  }
  return 'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-200 peer-focus-visible:ring-offset-1';
}

/**
 * Build outer circle classes
 */
function buildOuterClasses(outerSize: string, disabled: boolean, error: boolean): string {
  return [
    outerSize,
    outerBaseClasses,
    getOuterStateClasses(disabled, error),
    getFocusClasses(disabled),
  ].filter(Boolean).join(' ');
}

/**
 * Radio component for single-choice selections within a group.
 *
 * Built following the design system specifications:
 * - Size: 18px x 18px
 * - Border: 2px neutral-400 (#A8A29E)
 * - Border radius: full (circle)
 * - Selected: Outer circle fills entirely with primary-700 (same pattern as Checkbox — solid fill, no inner dot)
 * - Focus: 2px ring primary-200 (#BCCCDC)
 * - Label spacing: 8px from control
 *
 * @example
 * // Basic usage
 * <Radio label="Option A" name="options" value="a" />
 *
 * @example
 * // Selected state
 * <Radio label="Option B" name="options" value="b" checked />
 *
 * @example
 * // Error state
 * <Radio label="Required option" error helperText="This field is required" />
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      size = 'md',
      error = false,
      helperText,
      className = '',
      disabled = false,
      id: providedId,
      checked,
      defaultChecked,
      onChange,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    // Generate stable IDs for accessibility
    const reactId = React.useId();
    const radioId = providedId || `radio-${reactId}`;
    const helperId = `radio-helper-${reactId}`;

    // Compute aria-describedby
    const messageId = helperText ? helperId : undefined;
    const computedAriaDescribedBy = [ariaDescribedBy, messageId]
      .filter(Boolean)
      .join(' ') || undefined;

    // Get size classes
    const { outer: outerSize } = sizeClasses[size];

    // Build classes using helper functions
    const outerClasses = buildOuterClasses(outerSize, disabled, error);
    const containerClasses = buildContainerClasses(disabled, className);

    return (
      <div className="w-fit">
        <label className={containerClasses}>
          {/* Hidden input */}
          {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- We intentionally use aria-invalid for validation states */}
          <input
            ref={ref}
            type="radio"
            id={radioId}
            disabled={disabled}
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange}
            aria-invalid={error || undefined}
            aria-describedby={computedAriaDescribedBy}
            className="peer sr-only"
            {...props}
          />

          {/* Custom radio visual — fills entirely when checked, matching
              Checkbox. No inner indicator: the fill + coloured ring carry
              the selected state on their own. */}
          <span className={outerClasses} aria-hidden="true" />

          {/* Label */}
          {label && (
            <Text
              as="span"
              size="base"
              color={getLabelColor(disabled, error)}
              className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
            >
              {label}
            </Text>
          )}
        </label>

        {/* Helper text */}
        {helperText && (
          <Text
            id={messageId}
            size="xs"
            color={error ? 'error' : 'muted'}
            className="mt-1 ml-[26px]" // Align with label (18px box + 8px gap)
          >
            {helperText}
          </Text>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

export { Radio };
